/**
 * Thin typed wrapper for PostHog event capture.
 *
 * Standard PostHog posture — no forbidden-key guard (that was Ozer's HIPAA
 * artifact). This is naming discipline only: snake_case event names, typed
 * props via a discriminated union so call-sites can't typo a key.
 *
 * Usage:
 *   const posthog = usePostHog()   // at composable setup time
 *   captureEvent(posthog, { name: 'post_submitted', props: { source: 'url', channel: 'web' } })
 *
 * `usePostHog()` returns undefined on SSR — captureEvent handles that safely
 * via optional chaining on posthog?.capture().
 *
 * Extend the GabberEvent union as gabber's funnel grows. Server-confirmed
 * outcomes (purchase, credit debit, generation success) are emitted from the
 * NestJS backend; this file covers client-side interaction events only.
 */
import type { PostHog, CaptureOptions } from 'posthog-js';
import type { VanityCampaign, VanityLandingPath } from './vanity-landing';
import type { PhoneRejectionProps, PhoneStepStateProps } from './phone-step-telemetry';

// cas-2abd: where the subscription checkout was opened from. 'pricing' = the
// pricing/billing page dialog; 'make_it_mine' / 'make_it_better' = the
// audit-page unlock modal (AuditUnlockModal, keyed off which CTA the viewer
// clicked — mirrors the existing audit_unlock_clicked `entry` values);
// 'pre_expiry' = the pre-expiry "subscribe now" reminder modal;
// 'first_mib_foundation_gate' = the continuous first-MIB onboarding inline
// checkout card (after the Foundation Preview gate).
// 'begin_onboarding' = the public /begin chat onboarding inline checkout.
export type SubscriptionEntryPoint =
  | 'pricing'
  | 'make_it_mine'
  | 'make_it_better'
  | 'pre_expiry'
  | 'first_mib_foundation_gate'
  | 'begin_onboarding';

export interface PricingAnalyticsEventProps {
  pricing_country?: string;
  pricing_tier?: 1 | 2 | 3;
  pricing_currency?: string;
  local_intro_amount?: number;
  local_recurring_amount?: number;
  usd_anchor_tier?: 1 | 2 | 3;
}

export interface EligibleReelsEventProps {
  lookup_stage?: 'verify' | 'reels' | 'auto_audit' | 'phone';
  selection_mode?: 'choose_post' | 'auto_choose';
  count_scope?: 'selected';
  manychat_id?: string;
  ig: string;
  version?: string;
  channel: 'web';
  eligible_count: number;
  media_types?: Array<'reel' | 'carousel' | 'image'>;
  reel_count?: number;
  carousel_count?: number;
  image_count?: number;
  /** Request start to the rendered outcome; excludes earlier profile lookup. */
  duration_ms: number;
  /** Navigation to the rendered outcome, when browser timing is available. */
  page_load_ms?: number;
  status_code?: number;
  reason?: 'request_failed' | 'profile_not_found' | 'invalid_handle';
}

// cas-2abd: shared prop shape carried on every subscription checkout funnel event.
export interface SubscriptionEventProps extends PricingAnalyticsEventProps {
  entry_point: SubscriptionEntryPoint;
  /** Selected/known plan at event time; omitted only if genuinely not yet chosen. */
  plan?: 'solo' | 'pro';
  /** true when checkoutMode === 'setup' (free-first-month promo trial, no charge today). */
  is_trial: boolean;
  /** true when a promo code is currently applied on the checkout. */
  has_promo: boolean;
  workspaceId?: string;
  /** Safe checkout diagnostics; never raw provider/customer error text. */
  correlation_id?: string;
  error_code?: string;
}

/** cas-cb0a: how a browser-side checkout success was observed. */
export interface UiConfirmedOutcomeProps {
  outcome_source: 'ui_confirmed';
  /** CheckoutPanel checkoutMode: 'setup' is a card-backed trial (no charge today). */
  mode: 'payment' | 'setup';
  reason: 'stripe_confirmed' | 'already_subscribed' | 'promo_bypass';
}

/**
 * cas-7063 (Ben 2026-10-24 spec §6): the private-account creator discovery funnel. Never the raw
 * query, token, device or provider text; make_it_mine_started is emitted by the backend.
 */
export interface CreatorDiscoveryEventProps {
  event_version: 1;
  flow: 'private_account_mim';
  query_length?: number;
  outcome?: 'ok' | 'empty' | 'error';
  failure_code?: string;
  result_count?: number;
  rank?: number;
  is_verified?: boolean;
  has_follower_count?: boolean;
  post_count?: number;
  reel_count?: number;
  carousel_count?: number;
  image_count?: number;
  has_more?: boolean;
  page?: number;
  media_type?: 'reel' | 'carousel' | 'image';
  position?: number;
  from_view?: 'search' | 'posts';
  elapsed_ms?: number;
}

export type CreatorDiscoveryEventName =
  | 'creator_search_viewed'
  | 'creator_search_submitted'
  | 'creator_results_displayed'
  | 'creator_selected'
  | 'creator_posts_displayed'
  | 'inspiration_post_selected'
  | 'creator_search_fallback_used';

export type GabberEvent =
  | { name: CreatorDiscoveryEventName; props: CreatorDiscoveryEventProps }
  /**
   * cas-497f (S13b): a saved draft opened from the conversation (spec §21
   * `creative_deep_link_opened`). The backend emits the rest of the engine events;
   * no message text, links or phone numbers here.
   */
  | {
      name: 'creative_deep_link_opened';
      props: {
        event_version: 1;
        spec_event: 'creative_deep_link_opened';
        channel: 'SMS' | 'WEB_CHAT';
        source: 'chat_history';
      };
    }
  | {
      name: 'begin_step_viewed' | 'begin_exit';
      props: {
        state: string;
        surface: string;
        begin_session_id: string;
        post_id: string | null;
        reason?: 'drawer_close' | 'pagehide' | 'route_leave';
        ms_in_state?: number;
        last_action?: string | null;
        inspiration_accounts?: number;
        refs_selected?: number;
      };
    }
  | {
      name:
        | 'phone_step_viewed'
        | 'phone_submitted'
        | 'phone_accepted'
        | 'phone_step_back'
        | 'phone_validation_failed'
        | 'phone_submit_rejected';
      props: Omit<PhoneRejectionProps, 'reason'> & {
        surface: string;
        reason?: string;
        status_code?: number;
        error_code?: string;
        post_id?: string;
      };
    }
  | { name: 'phone_step_state'; props: PhoneStepStateProps }
  | {
      name: 'inspiration_discovery_result';
      props: {
        surface: string;
        workspaceId: string;
        failureCode: string | null;
        query_has_space: boolean;
        looks_like_url: boolean;
        query_length: number;
      };
    }
  | {
      name:
        | 'inspiration_candidate_rejected'
        | 'inspiration_confirm_blocked'
        | 'inspiration_reels_loaded'
        | 'inspiration_reels_empty'
        | 'inspiration_reels_error';
      props: {
        surface: string;
        workspaceId: string;
        reason?: 'need_account' | 'pick_at_least_one';
        failureCode?: string | null;
        reel_count?: number;
        load_more?: boolean;
      };
    }
  | {
      name: 'eligible_reels_loaded' | 'eligible_reels_empty' | 'eligible_reels_failed';
      props: EligibleReelsEventProps;
    }
  | { name: 'user_signed_in'; props: { method: 'email_password' | 'google' } }
  // cas-15cf: pre-expiry "subscribe now" prompt was shown (fires once per login,
  // when isOpen flips true). Gives the modal a top-of-funnel event so a future
  // Billing Pulse tile can measure shown → converted.
  | {
      name: 'pre_expiry_modal_shown';
      props: {
        /** Why the reminder fired: an at-risk cardless trial or a lapsing promo bypass. */
        source: 'cardless_trial' | 'promo_bypass';
        /** Whole days until entitlement end (ceil), null when unknown. */
        daysRemaining: number | null;
      };
    }
  | { name: 'post_submitted'; props: { source: 'text' | 'url' | 'file'; channel: 'web' | 'sms' } }
  // cas-82cd / GH #1960: the composer accepted input but could not route it, so
  // nothing was submitted. Without this the failure is invisible in the funnel —
  // it looks like the user simply never pressed send.
  | {
      name: 'composer_submit_blocked';
      props: {
        /**
         * Why routing failed.
         * - 'no_projects'          — the workspace genuinely has no platform to file under.
         * - 'projects_unavailable' — cas-845a: the platform list could not be LOADED
         *   (401/403/cold start/network). Kept separate because the two need different
         *   copy and because a rising rate here is an outage, not an onboarding gap.
         */
        reason: 'no_projects' | 'projects_unavailable';
        source: 'text' | 'url' | 'file';
        channel: 'web';
      };
    }
  | {
      name: 'audit_started';
      props: {
        /**
         * Distinguishes how the audit was initiated:
         * - 'file'        — user uploaded a video file directly
         * - 'video_url'   — user pasted a direct video URL (needs_confirmation → confirmed)
         * - 'social_link' — pasted social/media link resolved to auto_processing without
         *                   a confirmation step (e.g. Instagram, TikTok, YouTube shortlink)
         */
        url_kind: 'file' | 'video_url' | 'social_link';
        /** cas-5e63: REQUIRED — 'web' for browser-originated audits, 'sms' for Twilio inbound. */
        channel: 'web' | 'sms';
      };
    }
  | {
      name: 'idea_generated';
      props: { framework?: 'concept' | 'script' | 'caption'; channel: 'web' | 'sms' };
    }
  // cas-c402: improve-panel + paywall funnel events
  | {
      name: 'improve_panel_opened';
      props: {
        entry:
          | 'make_it_better'
          | 'make_it_mine'
          | 'make_it_mine_v2'
          | 'make_it_mine_v3'
          | 'audit_fixes';
        itemId: string;
        /**
         * cas-0998: true when this open was a RE-ENTRY into work that already
         * existed (resumed, nothing charged) rather than a fresh run. Absent on
         * fresh opens. Without this the two are indistinguishable downstream and
         * every resume reads as new Make It Better intent, inflating the funnel
         * exactly where we are trying to measure whether re-entry stopped
         * costing people credits.
         */
        resumed?: boolean;
      };
    }
  | {
      /** GH #1983: audit-origin Make It Mine routing diagnostic. */
      name: 'make_it_mine_route_selected';
      props: {
        route: 'v2' | 'v3' | 'legacy';
        state: 'fresh' | 'resumed';
        sourceAuditId: string;
        finalLanding: 'adaptation_panel' | 'post_content' | 'create' | 'create_brief';
      };
    }
  | { name: 'improve_message_sent'; props: { itemId: string } }
  | { name: 'improve_suggestion_applied'; props: { itemId: string } }
  | { name: 'improve_kept_original'; props: { itemId: string; phase: string } }
  | { name: 'improve_no_refilm_selected'; props: { itemId: string } }
  | {
      name: 'generation_brief_submitted';
      props: {
        itemId: string;
        flow: 'idea' | 'make_it_better';
        status: 'skipped' | 'completed';
        hasAudience: boolean;
        hasCta: boolean;
        hasInstructions: boolean;
        /** Required brief decision: growth | trust | sales. */
        purpose: string;
      };
    }
  | {
      name: 'thread_opened';
      props: {
        itemId: string;
        experience: 'thread';
        mode?: string | null;
        phase?: string;
      };
    }
  | {
      name: 'thread_duplicate_action_prevented';
      props: { itemId: string; experience: 'thread'; endpoint: string };
    }
  | {
      name: 'thread_option_selected';
      props: {
        itemId: string;
        experience: 'thread';
        mode?: string | null;
        phase?: string;
        field?: string;
        optionRef?: string;
        selectionKind?: 'original' | 'option';
      };
    }
  | {
      name: 'thread_option_group_rendered';
      props: {
        itemId: string;
        experience: 'thread';
        mode?: string | null;
        phase?: string;
        field?: string;
        optionCount: number;
        hasOriginal: boolean;
      };
    }
  | {
      name:
        | 'thread_phase_jump_requested'
        | 'thread_phase_jump_confirmed'
        | 'thread_phase_jump_cancelled';
      props: {
        itemId: string;
        experience: 'thread';
        mode?: string | null;
        phase?: string;
        requestedPhase?: string | null;
      };
    }
  | {
      name: 'improve_mode_selected';
      props: { itemId: string; mode: 'gabbers_choice' | 'see_options' | 'refilm' };
    }
  | { name: 'improve_draft_scheduled'; props: { itemId: string } }
  // cas-e1cf (B1): publish/schedule funnel — the core creator outcome (getting content
  // out to their platforms) was previously dark except for improve_draft_scheduled
  // (make-it-better path only). Gabber has no auto-post backend: publishing is a manual
  // Kanban status transition on the item-detail surfaces, so these are client-emitted
  // when an item moves INTO the Scheduled / Posted status (see utils/publishFunnel.ts).
  | {
      name: 'content_scheduled';
      props: {
        itemId: string;
        /** Target platform (instagram/tiktok/youtube/…) when the draft has one; omitted otherwise. */
        platform?: string;
        /** Always 'web' — scheduling happens in the studio UI. */
        channel: 'web';
      };
    }
  | {
      name: 'content_published';
      props: {
        itemId: string;
        /** Target platform when known; omitted otherwise. */
        platform?: string;
        /** Always 'web' — the creator confirms the post went live in the studio UI. */
        channel: 'web';
      };
    }
  | {
      name: 'paywall_displayed';
      props: { reason: string; workspaceId?: string } & PricingAnalyticsEventProps;
    }
  | {
      name: 'purchase_initiated';
      props: { plan?: string; pack?: string; workspaceId?: string } & PricingAnalyticsEventProps;
    }
  | {
      name: 'audit_report_ready';
      props: { url_kind?: string; postId: string; channel: 'web' | 'sms' };
    }
  // cas-2f40 (B5): content-engagement events on the detail surfaces. These give
  // A1's retention/stickiness a real "returned to consume value" anchor action
  // instead of counting $pageviews.
  | {
      name: 'audit_viewed';
      props: {
        postId: string;
        audit_id: string;
        workspace_id?: string;
        user_id?: string;
        anonymous_id?: string;
        schema_version: 1;
        $current_url: string;
        /**
         * Always 'web' — audits are consumed in the browser (SMS-origin audits are
         * opened on the web /audit/[postId] page too). Distinct from audit_report_ready:
         * audit_viewed fires for ANY viewer of a ready audit; audit_report_ready is
         * gated to the initiator (cas-3b0e6).
         */
        channel: 'web';
        /**
         * Present when the page was opened via an SMS completion link (`?s=` token).
         * Attribution is recorded server-side; this prop only tags the client event.
         */
        source?: 'sms';
      };
    }
  // cas-7e27: the "while you were busy, i worked on this for you" card under a
  // finished Make It Better / Make It Mine draft — shown, then (maybe) tapped.
  | {
      name: 'proactive_next_card_shown' | 'proactive_next_card_clicked';
      props: {
        workspaceId: string;
        /** The draft being viewed. */
        sourceKind: 'make_it_better' | 'make_it_mine';
        sourceItemId: string | null;
        /** What the card links to. */
        targetKind: 'audit' | 'inspo';
        targetId: string;
        headingKey: string;
      };
    }
  | {
      name: 'idea_opened';
      props: {
        itemId: string;
        /** The idea's content framework when known (e.g. concept/script/caption); free-form. */
        framework?: string;
      };
    }
  // cas-ce2b (B2): team-invite funnel — client-emit slice. member_joined is
  // backend-confirmed (separate task) and there is no dashboard card here (wave-2).
  | {
      name: 'invite_sent';
      props: {
        /** Workspace role granted to the invitee: user | admin | external. */
        role: string;
        workspaceId: string;
      };
    }
  | {
      name: 'invite_accepted';
      props: {
        /** Role from the invite, when the accepting surface knows it. */
        role?: string;
        workspaceId?: string;
        /**
         * Whether the accepting user has a phone. The new-user accept flow requires
         * one (always true there); the existing-user in-app accept reflects the
         * account's stored phone.
         */
        hasPhone: boolean;
      };
    }
  // cas-c87d: audit reveal/unlock funnel event — fires when user clicks
  // Make-It-Better or Make-It-Mine on a locked audit, before payment intent.
  | {
      name: 'audit_unlock_clicked';
      props: {
        postId: string;
        entry: 'make_it_better' | 'make_it_mine';
        workspaceId?: string;
        /** Always 'web' — this modal is browser-only; SMS path does not have a reveal step. */
        channel: 'web';
        /**
         * Set when the click routed somewhere other than the modal:
         * 'first_mib_continuous' = the leftover first-MIB page;
         * 'begin_audit_conversion' = the /begin conversion thread in the
         * audit-page bottom drawer;
         * 'sign_in_claimed_workspace' = signed-out visitor on a workspace
         * that already has an owner, sent to sign in.
         */
        variant?: 'first_mib_continuous' | 'begin_audit_conversion' | 'sign_in_claimed_workspace';
      } & PricingAnalyticsEventProps;
    }
  | {
      name: 'onboarding_started';
      props: {
        audit_id: string;
        workspace_id?: string;
        user_id?: string;
        anonymous_id?: string;
        entry_point: 'audit_unlock';
        channel: 'web';
        schema_version: 1;
        $insert_id: string;
      };
    }
  | {
      name: 'unlock_cta_clicked';
      props: {
        postId: string;
        workspaceId?: string;
        manychat_id?: string;
        channel: 'web';
      } & PricingAnalyticsEventProps;
    }
  | {
      name: 'handle_submitted' | 'start_auto_audit_failed';
      props: Record<string, unknown>;
    }
  | {
      name: 'ig_confirmed';
      props: {
        selection_mode?: 'auto-choose';
        manychat_id?: string;
        ig?: string;
        version?: string;
        channel: 'web';
      };
    }
  | {
      name: 'reel_selected';
      props: {
        selection_mode?: 'auto-choose';
        media_type?: 'reel' | 'carousel' | 'image';
        manychat_id?: string;
        ig?: string;
        version?: string;
        reel_url?: string;
        channel: 'web';
      };
    }
  // cas-7cbc: audit→subscribe funnel tracking
  | {
      name: 'promo_applied';
      props: {
        /**
         * 'trial'   — plan-bound code that switched checkout to mode='setup' (free first month).
         * 'discount' — plan-bound code that applied a price discount; also covers generic bypass codes.
         * 'credits'  — generic code that granted credits without changing checkout mode.
         */
        code_type: 'trial' | 'discount' | 'credits';
        workspaceId?: string;
        postId?: string;
        manychat_id?: string;
      };
    }
  // cas-e582: client-side payment-success view — fires on reaching /pricing/success,
  // /billing/packs/success, OR the embedded PackCheckoutModal inline-card success
  // (cas-cb85), filling the funnel gap between purchase_initiated and the
  // backend-confirmed purchase_completed (Stripe webhook). send_instantly prevents loss
  // on quick navigation away from the page. Backend purchase_completed remains revenue
  // source of truth; this event is for drop-off attribution only.
  | {
      name: 'purchase_success_viewed';
      props: {
        /** Subscription plan tier for /pricing/success; omitted for pack purchases. */
        plan?: 'solo' | 'pro' | 'team';
        /** Credit-pack purchase on /billing/packs/success or inline modal; omitted for plan purchases. */
        pack?: string;
        workspaceId?: string;
      };
    }
  // cas-cb85: embedded credit-pack checkout funnel (PackCheckoutModal). Interaction
  // steps between purchase_initiated (buy-credits clicked, fired by the host before the
  // modal opens) and the completion signal. Completion reuses purchase_success_viewed
  // (above) with a pack prop; backend purchase_completed remains revenue source of truth.
  | {
      name: 'pack_checkout_opened';
      props: {
        /** Pack SKU whose PaymentIntent was created and Payment Element mounted. */
        pack: string;
        /** Credit count of the pack (snapshot), for value attribution. */
        credits?: number;
        workspaceId?: string;
      };
    }
  | { name: 'pack_payment_submitted'; props: { pack: string; workspaceId?: string } }
  | {
      name: 'pack_checkout_failed';
      props: {
        pack: string;
        /**
         * Where in the embedded flow it failed:
         * 'intent'  — could not create the PaymentIntent (checkout never became payable).
         * 'payment' — Payment Element confirm returned an error (e.g. card declined).
         */
        stage: 'intent' | 'payment';
        workspaceId?: string;
      };
    }
  // cas-5192: unified submission event — one per universal-box submit, type-reliable for funnel analysis.
  | {
      name: 'universal_box_submitted';
      props: {
        /**
         * What the user submitted:
         * 'text'  — typed text input (idea/concept/script/caption copy).
         * 'voice' — Web Speech API transcription submitted as text; same backend path as 'text'.
         * 'link'  — pasted URL that resolved to a social/link audit or idea (not a raw video file).
         * 'video' — pasted direct-video URL or an uploaded video file (file.type video/*).
         * 'audio' — an uploaded audio file (file.type audio/*, e.g. .m4a voice memo). cas-18e8 review:
         *           the file input accepts video/* AND audio/*, so audio is its own bucket — do
         *           NOT collapse it into 'video' (corrupts funnel-by-type).
         * 'image' — reserved for future MMS image via SMS backend path; not fired from web today.
         */
        input_type: 'text' | 'voice' | 'link' | 'video' | 'audio' | 'image';
        /**
         * Where the backend routed this submission:
         * 'audit'   — auto_processing (immediately queued for audit).
         * 'idea'    — auto_created (classified as concept/script/caption).
         * 'pending' — needs_confirmation (waiting for user to choose audit/idea_bank).
         * 'redirect' — catch-all for any other backend action. NOTE: creator-text keyword
         *              submissions early-return before this event fires, so in practice 'redirect'
         *              is only reached by unexpected/future actions, not the creator-text path.
         */
        routing: 'audit' | 'idea' | 'pending' | 'redirect';
        /** 'web' only — SMS-origin submissions bypass this composable entirely. */
        channel: 'web';
      };
    }
  // cas-2abd: subscription (Solo/Pro) checkout funnel — CheckoutPanel.vue, shared
  // across the pricing page, the audit-page unlock modal, and the pre-expiry
  // reminder modal. Distinct from the credit-pack purchase_*/pack_* events above.
  | { name: 'subscription_checkout_opened'; props: SubscriptionEventProps }
  | {
      name: 'checkout_started';
      props: {
        audit_id?: string;
        workspace_id: string;
        user_id?: string;
        anonymous_id?: string;
        schema_version: 1;
        $insert_id: string;
        entry_point: SubscriptionEntryPoint;
        channel: 'web';
      };
    }
  | { name: 'subscription_plan_selected'; props: SubscriptionEventProps }
  | {
      name: 'subscription_promo_entered';
      props: SubscriptionEventProps & { valid: boolean; code: string };
    }
  // cas-8a6b: renamed from `subscription_payment_details_started`, which was
  // bound to the Stripe element's @ready and therefore meant "the form finished
  // mounting" — NOT that anyone started entering details. The old name asserted
  // user intent the data never carried and had already misled one investigation.
  // Verified before renaming: zero saved PostHog insights and zero admin queries
  // referenced the old name, so nothing silently zeroes.
  | {
      name:
        | 'subscription_payment_method_selected'
        | 'subscription_payment_field_error'
        | 'subscription_submit_validation_failed'
        | 'subscription_payment_methods_available'
        | 'checkout_abandoned_mid_entry';
      props: SubscriptionEventProps & Record<string, unknown>;
    }
  | { name: 'subscription_payment_form_rendered'; props: SubscriptionEventProps }
  // cas-154b: the Payment Element entry signals, each named for what it
  // measures. payment_form_changed replaces subscription_card_entry_started
  // (Stripe's first non-empty change — reported on load for most non-US
  // visitors, so never evidence of input). input_focused = a person tapped into
  // the form ("started entering payment details"); details_complete = every
  // input well-formed. payment_method is Stripe's value.type ('unknown' before
  // Stripe reports one).
  | {
      name: 'subscription_payment_form_changed';
      props: SubscriptionEventProps & { payment_method: string };
    }
  | {
      name: 'subscription_payment_input_focused';
      props: SubscriptionEventProps & { payment_method: string };
    }
  | {
      name: 'subscription_payment_details_complete';
      props: SubscriptionEventProps & { payment_method: string };
    }
  // cas-8a6b: the form could not be shown at all. Distinct from
  // subscription_checkout_failed, which means a payment was REJECTED — mixing
  // them would make a form that never rendered look like a declined card.
  | {
      name: 'subscription_payment_form_failed';
      props: SubscriptionEventProps & { reason: string };
    }
  | { name: 'subscription_payment_submitted'; props: SubscriptionEventProps }
  // cas-3e66: one-tap wallets (Express Checkout Element) above the card form.
  // `wallets` / `wallet` use Stripe's expressPaymentType names (apple_pay,
  // google_pay, link, …) so shown and clicked join on the same value. A wallet
  // payment ALSO emits subscription_payment_submitted and
  // subscription_checkout_succeeded/failed like a card; these say which wallet.
  | {
      name: 'subscription_express_checkout_shown';
      props: SubscriptionEventProps & { wallets: string };
    }
  | {
      name: 'subscription_express_checkout_clicked';
      props: SubscriptionEventProps & { wallet: string };
    }
  // cas-d05f: first wallet button press in a checkout — counted together with
  // subscription_payment_input_focused as the funnel's "started card or wallet" step.
  | {
      name: 'subscription_wallet_started';
      props: SubscriptionEventProps & { wallet: string };
    }
  | {
      name: 'subscription_express_checkout_succeeded';
      props: SubscriptionEventProps & { wallet: string; status: string };
    }
  | {
      name: 'subscription_express_checkout_failed';
      props: SubscriptionEventProps & { wallet: string; status: string };
    }
  /**
   * cas-cb0a: UI-confirmed outcome — what the browser saw, NOT a payment. It also
   * fires for card-backed trials (mode 'setup'), already-subscribed redirects and
   * promo bypasses, and is lost when the tab closes first. Never count it as Paid:
   * the server-side first_paid_invoice (from the Stripe receipt) is payment truth.
   */
  | {
      name: 'subscription_checkout_succeeded';
      props: SubscriptionEventProps & UiConfirmedOutcomeProps;
    }
  | {
      name: 'subscription_checkout_failed';
      props: SubscriptionEventProps & { error?: string };
    }
  /**
   * cas-ed03 (#1933): one series covering every state transition of a single
   * checkout attempt, keyed by a correlation id built from audit post +
   * workspace + subscription/intent. It exists so that mobile UI teardown,
   * explicit cancel, payment rejection, webhook lag, auth failure and post-load
   * failure stop looking identical from the outside.
   *
   * The props are built ONLY by utils/checkout-telemetry.ts buildCheckoutTransition,
   * which redacts card/ZIP/CVC/client-secret keys and digit runs unconditionally.
   * Do not construct this event by hand.
   */
  | {
      name: 'checkout_flow_transition';
      props: Record<string, string | number | boolean>;
    }
  // Brand Voice Chat (spec §12) — client-side interaction slice only. The
  // server-confirmed outcomes (session started, references confirmed, question
  // answered/skipped, setup saved, audit lifecycle, guide builds, variation
  // CRUD) are emitted from the NestJS brand-voice services; do NOT duplicate
  // them here or every action double-counts.
  | {
      name: 'brand_voice_chat_opened';
      props: { workspaceId: string; accessCohort: string; surface?: string };
    }
  | {
      name:
        | 'brand_voice_carousel_loaded'
        | 'brand_voice_carousel_empty'
        | 'brand_voice_carousel_error';
      props: { workspaceId: string; reelCount: number };
    }
  | {
      name: 'brand_voice_selection_changed';
      props: { workspaceId: string; selectedCount: number };
    }
  | {
      name: 'brand_voice_question_shown';
      props: { workspaceId: string; step: string };
    }
  | {
      name: 'brand_voice_review_shown';
      props: { workspaceId: string };
    }
  // Post Brief "How should this one sound?" — shown/selected are client
  // interactions; persisted fires after the save request succeeds (the brief
  // itself is the durable record).
  | {
      name: 'brand_voice_brief_selector_shown';
      props: {
        workspaceId?: string;
        itemId?: string;
        surface: 'form' | 'conversation';
        hasDefaultVoice: boolean;
        variationCount: number;
      };
    }
  | {
      name: 'brand_voice_brief_voice_selected' | 'brand_voice_brief_voice_persisted';
      props: {
        workspaceId?: string;
        itemId?: string;
        surface: 'form' | 'conversation';
        selection: 'default' | 'variation' | 'none';
      };
    }
  // Flexible creative workspace — user interactions from /create. Server-confirmed
  // entry, audit, request, and selection facts are emitted by the backend.
  | {
      name: 'creative_workspace_done_clicked';
      props: {
        workspaceId: string;
        itemId?: string;
        /**
         * How the creator finished: the Done button, or typing "done", which
         * the intent router resolves to the `draft` target. Both land on the
         * post Content view, so this is the only way to tell them apart.
         */
        trigger: 'button' | 'message';
        accepted_component_count: number;
        draft_component_count: number;
        draft_maturity?: string;
      };
    }
  | {
      name: 'creative_workspace_session_resumed';
      props: { workspaceId: string; itemId: string; conversation_turn_count: number };
    }
  /**
   * A finished post sent the creator into the next one instead of ending. The
   * source format is carried because it is what chose the shortcut: a carousel
   * is offered a script and everything else is offered a carousel.
   */
  | {
      name: 'creative_workspace_next_step_taken';
      props: {
        workspaceId: string;
        source_item_id?: string;
        next_step: 'rework' | 'to_carousel' | 'to_script';
        source_format_type?: string;
      };
    }
  /**
   * cas-d142: a Post Content Quick Action changed the format in one tap, at the
   * target format's default length (no length picker, no confirmation).
   */
  | {
      name: 'creative_workspace_post_quick_action_taken';
      props: {
        workspaceId: string;
        source_item_id?: string;
        quick_action: 'to_script' | 'to_carousel' | 'to_broll_text';
        target_format_type: string;
        target_format_option_id: string;
        source_format_type?: string;
      };
    }
  | {
      name: 'vanity_landing';
      props: { campaign: VanityCampaign; path: VanityLandingPath };
    };

/**
 * options is forwarded verbatim to PostHog's capture() third argument.
 * Use this for transport overrides that must survive a page unload:
 *   { transport: 'sendBeacon' }  — CreditsExhaustedDialog pack purchase (cas-5368)
 *   { send_instantly: true }     — pricing.vue plan purchase (cas-5368)
 */
export function captureEvent(
  posthog: PostHog | undefined,
  e: GabberEvent,
  options?: CaptureOptions
): void {
  posthog?.capture(e.name, e.props, options);
}
